// Stripe Configuration
import { loadStripe, Stripe } from '@stripe/stripe-js';

// Initialize Stripe with publishable key
let stripePromiseInternal: Promise<Stripe | null> | null = null;

export const getStripe = () => {
  if (!stripePromiseInternal) {
    const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

    if (!key) {
      console.error('⚠️ Stripe publishable key not found. Please set VITE_STRIPE_PUBLISHABLE_KEY in your environment variables.');
      return null;
    }

    stripePromiseInternal = loadStripe(key);
  }
  return stripePromiseInternal;
};

// Export the promise for use in Elements provider
// NOTE: Commented out to prevent warning on every page load when VITE_STRIPE_PUBLISHABLE_KEY is not set
// Uncomment when ready to enable payments
// export const stripePromise = getStripe();

// Pricing configuration
export const PRICING = {
  // One-time payment
  oneTime: {
    amount: 2999, // $29.99 in cents
    currency: 'usd',
    name: 'The Love Contract - Full Access',
    description: 'Complete access to create your personalized relationship agreement',
  },

  // Subscription tiers
  subscription: {
    free: {
      priceId: null,
      name: 'Free',
      price: 0,
      features: [
        'Complete relationship workflow',
        'Individual & joint questionnaire',
        'Contract generation & signing',
        'Current contract access',
      ],
    },
    premium: {
      priceId: import.meta.env.VITE_STRIPE_PREMIUM_PRICE_ID,
      name: 'Premium',
      price: 4.99,
      features: [
        'Everything in Free',
        'Unlimited contract version history',
        'Contract amendments',
        'Challenge check-ins',
      ],
    },
  },
};

// Valentine's Day Special Edition
export const VALENTINE_PRICING = {
  amount: 1999, // $19.99 in cents
  currency: 'usd',
  name: "Valentine's Day Collection",
  description: '24 romantic questions to celebrate your love story',
  productId: 'valentines',
  features: [
    '24 curated questions for couples',
    'Focus on appreciation & connection',
    'Perfect for a romantic evening',
    'Generates a keepsake love letter',
  ],
  availableUntil: '2026-02-28',
};

// Pre-Marriage Preparation Set
export const PREMARRIAGE_PRICING = {
  amount: 2399, // $23.99 in cents
  currency: 'usd',
  name: 'Pre-Marriage Preparation',
  description: '24 curated questions to prepare for your marriage journey',
  productId: 'pre-marriage',
  features: [
    '12 individual reflection questions',
    '12 joint collaboration questions',
    'Covers all 6 core relationship topics',
    'Integrated into your Love Contract',
  ],
};

// Life-stage question packs (Dating / Moving In / First Five Years).
// Defined in question-sets.ts so pricing, promo cards and question badges
// all read from one registry. Re-exported here for import consistency with
// VALENTINE_PRICING / PREMARRIAGE_PRICING above.
export {
  QUESTION_SETS,
  allQuestionSets,
  getQuestionSet,
  type QuestionSetDef,
} from './question-sets';

// Helper function to format price
export const formatPrice = (amount: number, currency: string = 'usd'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100);
};

// Check if user has active subscription (includes grace period for canceled subs)
export const hasActiveSubscription = (
  subscriptionStatus?: string,
  currentPeriodEnd?: string
): boolean => {
  if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') return true;

  // Canceled but still within the paid billing period
  if (subscriptionStatus === 'canceled' && currentPeriodEnd) {
    return new Date(currentPeriodEnd) > new Date();
  }

  return false;
};

// Check if user has purchased (one-time or subscription)
export const hasPremiumAccess = (user: {
  hasPurchased?: boolean;
  subscriptionStatus?: string;
  subscriptionCurrentPeriodEnd?: string;
}): boolean => {
  return user.hasPurchased === true || hasActiveSubscription(user.subscriptionStatus, user.subscriptionCurrentPeriodEnd);
};
